import { useCallback, useState } from 'react';
import cardIMG from '@/assets/imgs/cards//bg3.png';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components';
import {
  Button,
  Error,
  FieldImage,
  Image,
  SuccessStatus,
} from '@/components/generic';
import { zodResolver } from '@hookform/resolvers/zod';
import { useHandleVerifyCard } from '@package/smart-wallet/hooks';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/* Types & Schemas */
export const CardVerificationFormSchema = z.object({
  file: z.instanceof(File).refine((file) => file.size < 2000000, {
    message: 'File must be less than 2MB.',
  }),
});

export type CardVerificationFormType = z.infer<
  typeof CardVerificationFormSchema
>;

/* Internal components */
type CardVerificationFormProps = {
  handleVerifyCard: (
    title: string,
    cardNumber: string,
    cardId: number,
    file: File,
  ) => Promise<void>;
  onSuccess?: () => void;
  cardInfo: any;
  onClose: () => void;
  errorVerifyCard?: any;
};

const CardVerificationForm = ({
  handleVerifyCard,
  onSuccess,
  cardInfo,
  onClose,
  errorVerifyCard,
}: CardVerificationFormProps) => {
  const form = useForm<CardVerificationFormType>({
    resolver: zodResolver(CardVerificationFormSchema),
  });

  /* Callbacks */
  const onSubmit = useCallback(
    async (data: CardVerificationFormType) => {
      const { file } = data;

      // Use hardcoded values
      const title = 'Apple Pay';
      const cardNumber = cardInfo?.cardNumber || '';
      const cardId = cardInfo?.id || '';
      console.log(cardInfo);
      try {
        await handleVerifyCard(title, cardNumber, Number(cardId), file);
        form.reset();
        onSuccess?.(); // Call success callback after successful verification
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [form, handleVerifyCard, onSuccess, cardInfo],
  );

  /* Render */
  return (
    <Form {...form}>
      <form
        className='w-full flex flex-col gap-2'
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name='file'
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          render={({ field: { value, onChange, ...fieldProps } }) => (
            <FormItem>
              <FormControl>
                <FieldImage
                  label='Upload Confirmation Document'
                  tooltip='Please upload a screenshot of the binding Apple pay to Sunrate'
                  onChange={(file) => onChange(file)}
                  // classImage='max-h-100 [&>*]:!object-cover'
                  {...fieldProps}
                />
              </FormControl>
              <FormMessage className='text-primary-03/80' />
            </FormItem>
          )}
        />
        <div className='flex justify-end gap-3 mt-8'>
          <Button
            className='flex-1'
            isStroke
            type='button'
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            className='flex-1'
            isBlack={!form.formState.isSubmitting}
            isWhite={form.formState.isSubmitting}
            type='submit'
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? 'verifying...' : 'Verify'}
          </Button>
        </div>
        <Error
          error={errorVerifyCard}
          message={errorVerifyCard || 'Error While Verifying Card'}
        />
      </form>
    </Form>
  );
};
/* Main component */
export const ActivateCard = ({
  cardInfo,
  onClose,
}: {
  cardInfo: any;
  onClose: () => void;
}) => {
  const { handleVerifyCard, error: errorVerifyCard } = useHandleVerifyCard();
  const [isSuccess, setIsSuccess] = useState(false);
  if (isSuccess) {
    return (
      <SuccessStatus
        title='Card Activated'
        content='Your card has been successfully activated.'
        onClose={onClose}
        confirm={() => window.location.reload()}
      />
    );
  }
  return (
    <>
      <div className='mb-8 text-h4 max-md:text-h5 text-t-primary'>
        Activate Apple Card
      </div>
      <div className='flex items-center mb-2'>
        <div className='shrink-0'>
          <Image className='size-20 rounded-2xl' src={cardIMG} alt='' />
        </div>
        <div className='w-[calc(100%-5rem)] pl-6'>
          <div className='truncate text-h6 text-t-primary'>
            {cardInfo?.cardNumber}
          </div>
        </div>
      </div>
      <div className='mb-8 text-body-2 font-medium text-t-tertiary'>
        Attach a screenshot of your Wallet showing this card. <br />
        <span className='text-primary-02'>
          Do not remove the card from your phone
        </span>{' '}
        tuntil it’s verified. If everything is correct, the bank will verify it
        within — <span className='text-primary-02'>24 business hours.</span>
        <br />
        <br />
        No weekend service — requests sent on Friday are processed Sunday night
        to Monday morning.
      </div>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        <CardVerificationForm
          handleVerifyCard={handleVerifyCard}
          onSuccess={() => {
            setIsSuccess(true);
          }}
          cardInfo={cardInfo}
          onClose={onClose}
          errorVerifyCard={errorVerifyCard}
        />
      </section>
    </>
  );
};
